import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_REDIRECTS = {
  admin: "/admin",
  borrower: "/dashboard",
  lender: "/lender",
  investor: "/investor",
};

export default function ReviewPage() {
  const { role } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("Authenticating...");
  const [error, setError] = useState(null);

  useEffect(() => {
    async function autoLogin() {
      if (!role || !ROLE_REDIRECTS[role]) {
        setError(`Invalid role: "${role}". Use: admin, borrower, lender, investor`);
        return;
      }
      try {
        setStatus(`Generating review session for ${role}...`);
        const res = await axios.get(`${API}/dev/review-token/${role}`);
        if (res.data.error) {
          setError(res.data.error);
          return;
        }
        const { token, user } = res.data;
        localStorage.setItem("ic_token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setStatus(`Logged in as ${user.full_name} (${user.role}). Redirecting...`);
        window.location.href = ROLE_REDIRECTS[role];
      } catch (err) {
        setError(err.response?.data?.detail || err.message || "Failed to create review session");
      }
    }
    autoLogin();
  }, [role]);

  return (
    <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center" data-testid="review-page">
      <div className="text-center max-w-md mx-auto p-8">
        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h2 className="text-lg font-bold text-red-700 mb-2">Review Mode Error</h2>
            <p className="text-sm text-red-600">{error}</p>
            <div className="mt-4 text-xs text-[#999]">
              Available roles: <code>admin</code>, <code>borrower</code>, <code>lender</code>, <code>investor</code>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-700 mx-auto" />
            <h2 className="text-lg font-bold text-[#0A0A0A]">Review Mode</h2>
            <p className="text-sm text-[#666]">{status}</p>
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs text-amber-700 font-medium">
                This is a read-only review session using test data.
                <br />All actions use test accounts.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
